import { Empty, Input, Select, Tag, Typography } from 'antd'
import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DateTimeCell } from 'src/components'

import { ENTITY_META, ENTITY_ORDER } from './activityData'
import { EVENT_KIND_COLORS } from './constants'
import { useStyles } from './styles'
import type { BusinessEvent, EntityKind } from './types'

const { Text } = Typography

type Props = { events: BusinessEvent[] }

export const ActivityFeedTab = ({ events }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  const [actor, setActor] = useState<string>('all')
  const [entity, setEntity] = useState<EntityKind | 'all'>('all')
  const [search, setSearch] = useState('')

  const actors = useMemo(
    () => [...new Set(events.map((e) => e.actor))].sort(),
    [events]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()

    return events.filter((e) => {
      if (actor !== 'all' && e.actor !== actor) return false
      if (entity !== 'all' && e.entity !== entity) return false
      if (
        q &&
        !`${e.actor} ${e.action} ${e.detail ?? ''}`.toLowerCase().includes(q)
      ) {
        return false
      }

      return true
    })
  }, [events, actor, entity, search])

  return (
    <div>
      <div className={styles.feedFilters}>
        <Select<string>
          size="small"
          allowClear
          placeholder={t('workspaceOverview.companyGraph.filters.allActors')}
          value={actor === 'all' ? undefined : actor}
          onChange={(value) => setActor(value ?? 'all')}
          style={{ minWidth: 140 }}
          options={actors.map((a) => ({ value: a, label: a }))}
        />
        <Select<EntityKind | 'all'>
          size="small"
          allowClear
          placeholder={t('workspaceOverview.companyGraph.filters.allEntities')}
          value={entity === 'all' ? undefined : entity}
          onChange={(value) => setEntity(value ?? 'all')}
          style={{ minWidth: 130 }}
          options={ENTITY_ORDER.map((k) => ({
            value: k,
            label: ENTITY_META[k].label
          }))}
        />
        <Input
          size="small"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<Search size={13} />}
          placeholder={t('workspaceOverview.companyGraph.filters.search')}
          style={{ flex: 1, minWidth: 160 }}
        />
      </div>

      {filtered.length > 0 ? (
        <div className={styles.feedList}>
          {filtered.map((e) => {
            const meta = ENTITY_META[e.entity]
            const Icon = meta.icon

            return (
              <div key={e.id} className={styles.feedRow}>
                <span
                  className={styles.feedIcon}
                  style={{ background: `${meta.color}22`, color: meta.color }}
                  aria-hidden
                >
                  <Icon size={15} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text className={styles.feedActor}>{e.actor}</Text>{' '}
                  <Text className={styles.feedAction}>{e.action}</Text>
                </div>
                {e.detail ? (
                  <Tag
                    className={styles.feedDetail}
                    color={
                      e.kind === 'warn'
                        ? 'warning'
                        : e.kind === 'ok'
                          ? 'success'
                          : undefined
                    }
                    style={{
                      borderColor: `${EVENT_KIND_COLORS[e.kind]}55`
                    }}
                  >
                    {e.detail}
                  </Tag>
                ) : null}
                <DateTimeCell
                  value={e.ts}
                  variant="relative"
                  style={{ fontSize: 11, flex: '0 0 auto' }}
                />
              </div>
            )
          })}
        </div>
      ) : (
        <div className={styles.feedEmpty}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('workspaceOverview.companyGraph.feedEmpty')}
          />
        </div>
      )}
    </div>
  )
}
